import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import React from 'react';
import omit from 'lodash/omit';
import { withTranslation } from 'react-i18next';
import { KeyBindingUtil } from 'wix-rich-content-editor-common';
import { EditorToolbars, KeyboardSubmitStrategy } from './types';
import { DaEditor } from '../DaEditor';
import { Plugins } from '../DaEditor/enums';
import Modal from '../Popup/Modal';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import EmoteSelector, { EmoteSelectorPortal } from '../EmoteSelector';
import { elmoSaysThisMightBeADeviationUrl } from '../utils/deviations';
import MediaSelector from '../MediaPopup/MediaSelector';
import MediaManager from '../MediaPopup/MediaManager';
import { MAX_SELECTED_ITEMS } from '../MediaPopup/constants';
import { stripFormattingForEditorFlavour } from './Filter/formatting';
import { withMobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ThemeContext, ThemeOptions } from '@wix/da-react-context/pkg/ThemeContext';
import ddt from '@wix/da-ddt';
import { DA_EMOTE_TYPE } from '../DaEditor/plugins/emote';
import EmoteSelectorPopupWrapper from '../EmoteSelector/EmoteSelectorPopupWrapper';
import modalCss from './Themes/modals.scss';
class DaEditorWrapper extends React.Component {
    setup(props) {
        this.doFlavourSetup(props);
        if (this.isPapiEditorText(props.initialContent)) {
            this.hydrateInitialContent(props.initialContent);
        } else {
            this.initialContent = props.initialContent;
        }
        this.theme = this.props.theme;
    }
    doFlavourSetup(props) {
        const { flavour } = props;
        this.activePlugins = Object.keys(flavour.pluginsConfig).reduce((acc, pl)=>{
            acc[pl] = true;
            return acc;
        }, {});
        this.embedToolbars = flavour.toolbars.reduce((acc, t)=>{
            acc[t] = true;
            return acc;
        }, {});
        this.features = flavour.featureConfig;
    }
    filterProtectedPlugins(plugins) {
        // const { featureFlags } = this.props;
        return plugins;
    }
    isPapiEditorText(content) {
        return content && content.excerpt !== undefined;
    }
    hydrateInitialContent(initialContent) {
        if (!initialContent) {
            return;
        }
        if (initialContent.html.type !== 'draft') {
            alert('Tried to edit old format with new editor');
            return;
        }
        if (!initialContent.html.markup) {
            return;
        }
        try {
            this.initialContent = JSON.parse(initialContent.html.markup);
            this.initialFeatures = JSON.parse(initialContent.html.features || '[]');
        } catch (e) {
            alert('Failed to load markup');
        }
    }
    componentDidMount() {
        this.setState({
            mounted: true
        });
    }
    renderToolbarSide() {
        if (!this.embedToolbars[EditorToolbars.Side]) {
            return;
        }
        const { toolbars } = this.state;
        if (toolbars && toolbars.SIDE) {
            const Toolbar = toolbars.SIDE;
            return /*#__PURE__*/ React.createElement(Toolbar, null);
        }
    }
    renderToolbarMobile() {
        if (!this.embedToolbars[EditorToolbars.Mobile]) {
            return;
        }
        const { toolbars } = this.state;
        if (toolbars && toolbars.MOBILE) {
            const Toolbar = toolbars.MOBILE;
            return /*#__PURE__*/ React.createElement(Toolbar, null);
        }
    }
    renderToolbarTextStatic() {
        if (!this.embedToolbars[EditorToolbars.Static]) {
            return;
        }
        const { toolbars } = this.state;
        if (toolbars && toolbars.TEXT) {
            const Toolbar = toolbars.TEXT;
            return /*#__PURE__*/ React.createElement(Toolbar, null);
        }
    }
    renderToolbarTextInline() {
        if (!this.embedToolbars[EditorToolbars.Inline]) {
            return;
        }
        const { toolbars } = this.state;
        if (toolbars && toolbars.INLINE) {
            const Toolbar = toolbars.INLINE;
            return /*#__PURE__*/ React.createElement(Toolbar, null);
        }
    }
    renderEmoteSelector() {
        const { emoteSelectProps } = this.state;
        return emoteSelectProps && /*#__PURE__*/ React.createElement(EmoteSelector, emoteSelectProps);
    }
    renderEmojiSelector() {
        var _modalProps_buttonRef;
        const { modalProps, ModalContent } = this.state;
        return modalProps && ModalContent && /*#__PURE__*/ React.createElement(EmoteSelectorPopupWrapper, {
            buttonRect: (_modalProps_buttonRef = modalProps.buttonRef) === null || _modalProps_buttonRef === void 0 ? void 0 : _modalProps_buttonRef.getBoundingClientRect(),
            contentClassname: modalCss['emoji'],
            onClickOutside: ()=>this.setState({
                    showEmojiSelector: false
                })
        }, /*#__PURE__*/ React.createElement(ErrorBoundary, {
            debugComponent: "EmojiSelectorEditor"
        }, /*#__PURE__*/ React.createElement(ModalContent, null)));
    }
    renderMediaPopup() {
        const { mediaPopupProps } = this.state;
        if (!mediaPopupProps) {
            return null;
        }
        return mediaPopupProps['initialDeviationSet'] ? /*#__PURE__*/ React.createElement(MediaManager, mediaPopupProps) : /*#__PURE__*/ React.createElement(MediaSelector, _object_spread_props(_object_spread({}, mediaPopupProps), {
            title: this.props.t('mediapopup.selectMediaContent'),
            mediaSourceTypes: this.props.flavour.mediaSourceTypes
        }));
    }
    render() {
        var _this_state_modalProps, _this_state, _this_state_modalProps1, _this_state1;
        const { flavour, isDarkTheme: isDarkThemeFromProps, singleLine, plainText, filterPaste, charLimit, featureFlags, onKeyboardSubmit, i18n, isMobile, className } = this.props;
        const { showModal, ModalContent, mounted, showEmoteSelector, showEmojiSelector, showMediaPopup } = this.state;
        if (!mounted) {
            return null; // Never SSR
        }
        const restProps = omit(this.props, [
            'flavour',
            'onEditorInit',
            'onMentionRequested',
            'onEmotesRequested',
            'onImageRequested',
            'onModalOpen',
            'onModalClose',
            'isDarkTheme',
            'initialContent',
            'inlineToolbarButtons',
            'plugins',
            'onContentChange',
            'theme',
            'editorFocused',
            'editorBlurred',
            'onFocus',
            'onBlur',
            'singleLine',
            'plainText',
            'filterPaste',
            'charLimit',
            'featureFlags',
            'onKeyboardSubmit',
            'className'
        ]);
        let isDarkTheme = isDarkThemeFromProps;
        if (featureFlags && isDarkTheme !== undefined) {
            isDarkTheme = this.props.currentTheme === ThemeOptions.DARK;
        }
        if (singleLine || onKeyboardSubmit) {
            restProps.handleReturn = this.handleReturn;
        }
        if (plainText) {
            restProps.stripPastedStyles = true;
        }
        if (filterPaste) {
            restProps.filterPaste = stripFormattingForEditorFlavour.bind(null, flavour);
        }
        const extraModalProps = {};
        if ((_this_state = this.state) === null || _this_state === void 0 ? void 0 : (_this_state_modalProps = _this_state.modalProps) === null || _this_state_modalProps === void 0 ? void 0 : _this_state_modalProps.asPanel) {
            extraModalProps.overlayClassName = modalCss['light-overlay'];
            extraModalProps.className = modalCss['panel-modal'];
            extraModalProps.portalClassName = modalCss['modal-portal'];
        }
        if (((_this_state1 = this.state) === null || _this_state1 === void 0 ? void 0 : (_this_state_modalProps1 = _this_state1.modalProps) === null || _this_state_modalProps1 === void 0 ? void 0 : _this_state_modalProps1.modalName) === 'text-link-modal') {
            extraModalProps.className = modalCss['link-panel'];
        }
        return /*#__PURE__*/ React.createElement(ThemeContext.Consumer, null, (theme)=>/*#__PURE__*/ React.createElement(ErrorBoundary, {
                debugComponent: "DaEditorWrapper"
            }, this.renderToolbarSide(), this.renderToolbarMobile(), this.renderToolbarTextStatic(), this.renderToolbarTextInline(), showModal && /*#__PURE__*/ React.createElement(Modal, _object_spread({
                withCloseButton: false,
                isOpen: true,
                contentLabel: "Editor Modal",
                onRequestClose: this.closeModal,
                shouldReturnFocusAfterClose: false
            }, extraModalProps), /*#__PURE__*/ React.createElement(ModalContent, null)), /*#__PURE__*/ React.createElement("div", {
                className: className
            }, /*#__PURE__*/ React.createElement(DaEditor, _object_spread({
                currentUser: this.props.currentUser,
                hideSideToolbar: isMobile,
                "data-enable-grammarly": "false",
                features: this.features,
                initialContent: this.initialContent,
                onEditorInit: this.handleEditorInit,
                onMentionRequested: this.handleMention,
                onEmotesRequested: this.handleEmote,
                onImageRequested: this.handleImageSelection,
                onModalOpen: this.onModalOpen,
                onModalClose: this.onModalClose,
                onContentChange: this.handleContentChange,
                isMobile: this.context.isMobile,
                isDarkTheme: isDarkTheme === undefined ? theme === 'dark' : isDarkTheme,
                inlineToolbarButtons: flavour.textTools,
                inlineToolbarDecoratorFn: flavour.textToolbarDecoratorFn,
                showToolbarTooltips: this.props.showToolbarTooltips,
                plugins: this.filterProtectedPlugins(flavour.pluginsConfig),
                theme: this.theme,
                onFocus: this.onFocus,
                onBlur: this.onBlur,
                charLimit: charLimit,
                deviationPasteValidator: this.validateDeviationUrlPaste,
                onDeviationPasted: this.handleDeviationUrlPaste,
                onFetchFreshDeviations: this.handleFetchFreshDeviations,
                featureFlags: featureFlags,
                localeResource: i18n.getResourceBundle(i18n.language, 'translation'),
                onFileDrop: this.handleFileDrop
            }, restProps, flavour.extraProps))), /*#__PURE__*/ React.createElement(EmoteSelectorPortal, null, showEmoteSelector && this.renderEmoteSelector(), showEmojiSelector && this.renderEmojiSelector()), showMediaPopup && this.renderMediaPopup()));
    }
    constructor(props){
        super(props);
        _define_property(this, "activePlugins", void 0);
        _define_property(this, "embedToolbars", void 0);
        _define_property(this, "editorState", void 0);
        _define_property(this, "initialContent", void 0);
        _define_property(this, "initialFeatures", void 0);
        _define_property(this, "features", void 0);
        _define_property(this, "theme", void 0);
        _define_property(this, "initialContentState", void 0);
        _define_property(this, "initialRawState", void 0);
        _define_property(this, "androidFixDone", false);
        _define_property(this, "state", {
            showModal: false,
            showEmoteSelector: false,
            showEmojiSelector: false,
            showMediaPopup: false,
            mounted: false
        });
        _define_property(this, "onModalOpen", (ModalContent, props, styles)=>{
            var _props_buttonRef_closest;
            // Emotes dont use popup so intercept and display differently
            if (props && props.modalName === DA_EMOTE_TYPE) {
                var _props_buttonRef;
                this.handleEmote(props.modalElement, (_props_buttonRef = props.buttonRef) === null || _props_buttonRef === void 0 ? void 0 : _props_buttonRef.getBoundingClientRect());
                return;
            }
            // Emoji uses the emote wrapper for popover display
            if (props && props.buttonRef && ((_props_buttonRef_closest = props.buttonRef.closest('button')) === null || _props_buttonRef_closest === void 0 ? void 0 : _props_buttonRef_closest.getAttribute('data-hook')) === 'EmojiPlugin_InsertButton') {
                this.setState({
                    showEmojiSelector: true,
                    ModalContent,
                    modalProps: props,
                    modalStyles: styles
                });
                return;
            }
            this.setState({
                showModal: true,
                ModalContent,
                modalProps: props,
                modalStyles: styles
            });
        });
        _define_property(this, "onModalClose", ()=>{
            if (this.state.showEmojiSelector) {
                requestAnimationFrame(()=>{
                    var _this_state_api, _this_state;
                    (_this_state = this.state) === null || _this_state === void 0 ? void 0 : (_this_state_api = _this_state.api) === null || _this_state_api === void 0 ? void 0 : _this_state_api.focusEditor();
                });
            }
            this.setState({
                showEmojiSelector: false,
                showModal: false,
                ModalContent: null,
                modalProps: null,
                modalStyles: undefined
            });
        });
        _define_property(this, "closeModal", ()=>{
            this.setState({
                showModal: false
            });
        });
        _define_property(this, "onFocus", (e)=>{
            const { onFocus, editorFocused, editorKey } = this.props;
            onFocus && onFocus(e);
            editorFocused && editorKey && editorFocused(editorKey);
        });
        _define_property(this, "onBlur", (e)=>{
            const { onBlur, editorBlurred, editorKey } = this.props;
            onBlur && onBlur(e);
            editorBlurred && editorKey && editorBlurred(editorKey);
        });
        _define_property(this, "handleMention", ({ value }, setMentions)=>{
            if (!this.activePlugins[Plugins.MENTION]) {
                return;
            }
            this.props.mentionSearchChange && this.props.mentionSearchChange({
                value,
                setMentions
            });
        });
        _define_property(this, "handleEmote", (onEmoteSelection, targetRect)=>{
            const { isMobile } = this.props;
            this.setState({
                showEmoteSelector: true,
                emoteSelectProps: {
                    onEmoteSelect: (e)=>{
                        onEmoteSelection(e);
                        this.state.api.focusEditor();
                        isMobile && this.setState({
                            showEmoteSelector: false,
                            showModal: false
                        });
                    },
                    buttonRect: targetRect,
                    onClose: ()=>{
                        this.setState({
                            showEmoteSelector: false
                        });
                    }
                }
            });
        });
        _define_property(this, "handleFileDrop", (uploadHelpers)=>(file)=>{
                const { uploadFile } = this.props;
                uploadFile && uploadFile(file, uploadHelpers);
            });
        _define_property(this, "handleImageSelection", (multiple, updateEntity, componentData)=>{
            const { isMobile } = this.props;
            // multiple is always true so we need to duck type
            multiple = componentData.items ? true : false;
            let mediaPopupProps;
            if (componentData && componentData.data && Array.isArray(componentData.data)) {
                mediaPopupProps = {
                    selectionLimit: multiple ? MAX_SELECTED_ITEMS : 1,
                    onDone: (selectedDeviations)=>{
                        updateEntity({
                            data: selectedDeviations
                        });
                        isMobile ? this.setState({
                            showMediaPopup: false,
                            showModal: false
                        }) : this.setState({
                            showMediaPopup: false
                        });
                    },
                    onClose: ()=>{
                        this.setState({
                            showMediaPopup: false
                        });
                    },
                    initialDeviationSet: componentData.data
                };
            } else {
                mediaPopupProps = {
                    selectionLimit: multiple ? MAX_SELECTED_ITEMS : 1,
                    onSelect: (selectedDeviations)=>{
                        updateEntity({
                            data: selectedDeviations
                        });
                        isMobile ? this.setState({
                            showMediaPopup: false,
                            showModal: false
                        }) : this.setState({
                            showMediaPopup: false
                        });
                    },
                    onClose: ()=>{
                        this.setState({
                            showMediaPopup: false
                        });
                    }
                };
            }
            this.setState({
                showMediaPopup: true,
                mediaPopupProps
            });
        });
        _define_property(this, "handleContentChange", (rawContentState, editorContent, featuresData, selectionState)=>{
            ddt.log('editor', 'Content changed', rawContentState);
            ddt.log('editor', 'Features changed', featuresData);
            if (!this.initialContentState) {
                this.initialContentState = editorContent;
                this.initialRawState = rawContentState;
                this.props.onInitialContentReady && this.props.onInitialContentReady(this.initialContentState, this.initialRawState);
            }
            this.props.onContentChange && this.props.onContentChange(rawContentState, editorContent, featuresData, selectionState);
        });
        _define_property(this, "handleEditorInit", (toolbars, api)=>{
            const { onEditorInit, autoFocus, selectionState, editorKey } = this.props;
            onEditorInit && onEditorInit(toolbars, api);
            // Fuck grammarly, buy a fucking book god damit
            if (/^[a-z]*/i.test(editorKey || '')) {
                const editable = document.querySelector(`#${editorKey} .DraftEditor-editorContainer > div[contenteditable]`);
                if (editable) {
                    editable.setAttribute('data-enable-grammarly', 'false');
                }
            }
            requestAnimationFrame(()=>{
                try {
                    if (autoFocus) {
                        api.focusEditor();
                        api.moveFocusToEnd();
                    }
                    selectionState && api.setSelection(selectionState);
                } catch (e) {
                /* anim frame is async so handle instance when editor is gone */ }
            });
            this.setState({
                toolbars,
                api
            });
        });
        _define_property(this, "validateDeviationUrlPaste", (text)=>{
            if (!/^(https?:\/\/|:thumb)/.test(text)) {
                return;
            }
            return elmoSaysThisMightBeADeviationUrl(text);
        });
        _define_property(this, "handleDeviationUrlPaste", (deviationLookup, setEntityData)=>{
            this.props.findPastedDeviation && this.props.findPastedDeviation({
                deviationLookup,
                setEntityData
            });
        });
        _define_property(this, "handleFetchFreshDeviations", (deviations, setEntityData)=>{
            this.props.fetchFreshDeviations && this.props.fetchFreshDeviations({
                deviations,
                setEntityData
            });
        });
        _define_property(this, "handleReturn", (updateEditorStateCallback)=>{
            return (e, editorState)=>{
                const { singleLine, onKeyboardSubmit, keyboardSubmitStrategy = KeyboardSubmitStrategy.DEFAULT } = this.props;
                if (onKeyboardSubmit) {
                    switch(keyboardSubmitStrategy){
                        case KeyboardSubmitStrategy.CHAT:
                            if (!e.shiftKey) {
                                onKeyboardSubmit();
                                return 'handled';
                            }
                            break;
                        case KeyboardSubmitStrategy.DEFAULT:
                        default:
                            if (KeyBindingUtil.hasCommandModifier(e) || e.altKey) {
                                onKeyboardSubmit();
                                return 'handled';
                            }
                            break;
                    }
                }
                if (singleLine) {
                    return 'handled';
                }
                return 'not-handled';
            };
        });
        this.setup(props);
    }
}
export default withTranslation()(withMobileContext(DaEditorWrapper));
